import {
  AlertTitle,
  alertClasses,
  Button,
  InputAdornment,
  Stack,
  styled,
  ListItemText,
  ListItemAvatar,
  Avatar,
  Divider,
  ListSubheader,
  MenuItem,
} from '@mui/material'
import Alert from '@mui/material/Alert'
import Box from '@mui/material/Box'
import DialogContent from '@mui/material/DialogContent'
import Grid from '@mui/material/Grid'
import Skeleton from '@mui/material/Skeleton'
import Typography from '@mui/material/Typography'
import {
  getChannelCampaign,
  activateCampaign,
  ACTIVATE_CAMPAIGN_FULFILLED,
  ACTIVATE_CAMPAIGN_REJECTED,
} from '@promoboxx/redux-stores/src/stores/facebook-ads/actions'
import { getLocationsByRetailer } from '@promoboxx/redux-stores/src/stores/locations/retailers/actions'
import { Formik, Field } from 'formik'
import { TextField } from 'formik-mui'
import I18n from 'i18n-js'
import { useEffect, useState, useRef, memo, Fragment, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { makeStyles } from 'tss-react/mui'
import * as Yup from 'yup'

import MenuItemWithCheckbox from '@src/lib/MenuItemWithCheckbox'
import { useFeatureFlag } from '@src/lib/useFeatureFlag'
import { formatWithCents, campaignMinimumSpend } from '@src/lib/Utils'
import { userIsInternalSelector } from '@src/stores/app/selectors'
import {
  facebookPagesByRetailerSelector,
  facebookPagesById,
} from '@src/stores/facebookPages/selectors'

import { awaitActionType } from '../lib/awaitActionTypeMiddleware'
import useCombinedPremiumAdData from '../lib/useCombinedPremiumAdData'
import {
  retailerIdsByBrandIdSelector,
  brandsById,
  currentRetailerAccountSelector,
} from '../stores/app/selectors'

import PremiumAdAutomationBanner from './automations/PremiumAdAutomationBanner'
import CompletedState from './content/ActivatePaidAdModal/CompletedState'
import AutomationBrandFacebookPageCheckbox from './content/AutomationBrandFacebookPageCheckbox'
import ChannelPageSelectorField from './content/ChannelPageSelector/ChannelPageSelectorField'
import PaidAdCredit from './content/PaidAdCredit'
import PaidAdCTA from './content/PaidAdCTA/PaidAdCTA'
import DateHandler from './content/PaidAdDetails/CampaignSchedule/DateHandler'
import { generateCreative } from './content/PaidAdHOC/utils/generateCreative'
import isPaidAdActivatable from './isPaidAdActivatable'
import isPaidAdActivated from './isPaidAdActivated'
import PaidAdPreview from './PaidAdPreview'
import DepositFundsButton from './payment/DepositFundsButton'
import RetailerOverrides from './RetailerOverrides'
import useTracking from './shared/Tracking/useTracking'
import useRetailerAccountWallet from './wallet/useRetailerAccountWallet'

const useStyles = makeStyles()(() => ({
  root: {
    backgroundColor: 'white',
    // TODO `theme.shadows[n]` / <Paper elevation={n} /> / <Box boxShadow={n} />
    boxShadow: 'rgba(0, 0, 0, 0.05) 0px 2px 8px 0px',
    // Arbitrary number to match the rest of the designs
    marginBottom: 24,
  },
  headerRoot: {
    background: `radial-gradient(
      at 50% 100%,
      #ffffff 0%,
      rgba(255, 255, 255, 0.04) 90%,
      rgba(255, 255, 255, 0) 100%
    ),
    linear-gradient(44.88deg, #d9e1e9 0%, #edf4fc 100%)`,
  },
}))

const DialogContentNoPadding = styled(DialogContent)({
  'padding': 0,
  '&:first-child': {
    padding: 0,
  },
})

const urlRegexp =
  // eslint-disable-next-line no-useless-escape
  /^(?:http(s)?:\/\/)?[\w.-]+(?:\.[\w\.-]+)+[\w\-\._~:/?#[\]@!\%text'\(\)\*\+,;=.&]+$/i

const schema = Yup.object().shape({
  adCreativeTemplate: Yup.object().shape({
    creative: Yup.object().shape({
      link: Yup.object().shape({
        url: Yup.string().matches(urlRegexp, I18n.t('content.validUrl')),
      }),
      child_attachments: Yup.array().of(
        Yup.object().shape({
          link: Yup.object().shape({
            url: Yup.string().matches(urlRegexp, I18n.t('content.validUrl')),
          }),
        }),
      ),
    }),
  }),
})

/**
 * @typedef {{
 *  campaignId: string | number
 *  extraButtons?: React.ReactNode
 *  isDialog?: boolean
 * }} FPPAPProps
 */

/** @type {React.FC<FPPAPProps>} */
const FPPAP = (props) => {
  // '' | 'activated' | 'error'
  const [activationStatus, setActivationStatus] = useState('')
  const { classes } = useStyles()
  const {
    campaign,
    channelCampaign,
    campaignChannelId,
    adCreativeTemplate,
    adSetTemplate,
  } = useCombinedPremiumAdData(props.campaignId)
  /** @type {import('@promoboxx/graphql-gateway-types').RetailerAccount} */
  const currentRetailerAccount = useSelector(currentRetailerAccountSelector)
  const { track } = useTracking()
  const dispatch = useDispatch()
  const formikRef = useRef(null)
  const isInternal = useSelector(userIsInternalSelector)
  const pagesById = useSelector(facebookPagesById)
  const facebookPagesFromRetailerAccount = Array.from(
    currentRetailerAccount.facebook_pages || [],
  ).map((page) => pagesById[page.id])
  const brand = useSelector((state) => {
    if (!campaign) {
      return null
    }

    return brandsById(state)[campaign.brand_id]
  })

  const retailerId = useSelector((state) => {
    if (!brand) {
      return null
    }

    return retailerIdsByBrandIdSelector(state)[brand.id]
  })

  /** @type {import('@promoboxx/graphql-gateway-types').Retailer} */
  const currentRetailer =
    currentRetailerAccount.retailers.find(
      (x) => String(x.id) === String(retailerId),
    ) || {}

  const facebookPagesFromRetailerSelector = useSelector(
    facebookPagesByRetailerSelector,
  )[currentRetailer.id]

  useEffect(() => {
    if (channelCampaign || !campaignChannelId || !retailerId) {
      return
    }
    dispatch(
      getChannelCampaign({
        channelCampaignId: campaignChannelId,
        retailerIds: [retailerId],
      }),
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [channelCampaign, campaignChannelId, retailerId])

  // TODO Doesn't need to be done in here.
  useEffect(() => {
    if (retailerId) {
      dispatch(getLocationsByRetailer({ retailerId }))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [retailerId])

  const locations =
    useSelector((state) => {
      return retailerId
        ? (state.locations.retailers[retailerId] || {}).response || null
        : null
    }) || []

  const isAuthenticationAtRaEnabled = useFeatureFlag('authentication-at-ra')

  const facebookPages = useMemo(() => {
    // Making a new array because we will potentially sort it.
    /** @type {import('@promoboxx/graphql-gateway-types').FacebookPage[]} */
    const list = [
      ...((isAuthenticationAtRaEnabled
        ? facebookPagesFromRetailerAccount
        : facebookPagesFromRetailerSelector) || []),
    ]

    if (isAuthenticationAtRaEnabled) {
      // Sort so pages already connected to the brand are at the top.
      list.sort((a, b) => {
        if (facebookPagesFromRetailerSelector?.some((x) => x.id === a.id)) {
          return 1
        }

        return -1
      })
    }

    return list
  }, [
    facebookPagesFromRetailerAccount,
    facebookPagesFromRetailerSelector,
    isAuthenticationAtRaEnabled,
  ])

  // Split the pages into preferred and unpreferred
  const startOfUnprefferedIndex = facebookPages.findIndex(
    (page) => !facebookPagesFromRetailerSelector?.some((x) => x.id === page.id),
  )
  const preferredPages = facebookPages.slice(0, startOfUnprefferedIndex)
  const unpreferredPages = facebookPages.slice(startOfUnprefferedIndex)

  const [
    adCreativeTemplateWithRetailerUrl,
    setAdCreativeTemplateWithRetailerUrl,
  ] = useState(null)
  useEffect(() => {
    setAdCreativeTemplateWithRetailerUrl(
      mergeRetailerOverride(
        adCreativeTemplate,
        currentRetailerAccount.website,
        isActivated,
      ),
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [adCreativeTemplate, currentRetailerAccount])

  let isLoading = true
  if (campaign && adCreativeTemplateWithRetailerUrl && adSetTemplate && brand) {
    isLoading = false
  }

  let isActivated = isPaidAdActivated(campaign)
  let isActivatable = isPaidAdActivatable(campaign) && locations.length > 0

  const minimumSpend = campaignMinimumSpend(
    campaign?.starts_at,
    campaign?.ends_at,
  )

  const { findLedgersApplicableToPaidAd, refetchWallet } =
    useRetailerAccountWallet()

  const campaignType = campaign ? campaign.type : 'standard'
  const availableLedgers = [
    ...(campaignType === 'brand_and_retailer_funded' ||
    campaignType === 'retailer_funded'
      ? findLedgersApplicableToPaidAd(brand ? brand.uuid : '', {
          excludeExpired: true,
          excludeEmpty: true,
        })
      : []),
  ]
  const availableFunds =
    availableLedgers.reduce((memo, x) => memo + Number(x.balance), 0) / 100

  const fundingBlockColor =
    campaignType === 'retailer_funded' ? 'secondary' : 'primary'

  if (activationStatus === 'activated') {
    isActivated = true
  }

  const bodyMargin = 4
  const headerMargin = 4
  let ContentContainer = Fragment
  let RootContainer = 'div'
  let rootContainerProps = {
    'className': classes.root,
    'data-testid': 'featuredAdWrapper',
  }

  if (props.isDialog) {
    ContentContainer = DialogContentNoPadding
    RootContainer = 'div'
    rootContainerProps = { 'data-testid': 'reviewAdWrapper' }
  }

  let headerLine1
  let headerLine2
  if (isLoading) {
    headerLine1 = <Skeleton width={50} />
    headerLine2 = <Skeleton width={100} />
  } else {
    headerLine1 = props.isDialog
      ? brand.name
      : I18n.t('content.featuredPremiumAd')
    headerLine2 = props.isDialog
      ? `| ${campaign.title}`
      : `${brand.name} | ${campaign.title}`
  }

  let genderNames
  if (!isLoading) {
    const genders = adSetTemplate.genders || []
    switch (genders.join()) {
      case '1':
        genderNames = I18n.t('content.male')
        break
      case '2':
        genderNames = I18n.t('content.female')
        break
      case '1,2':
      default:
        genderNames = [I18n.t('content.male'), I18n.t('content.female')].join(
          I18n.t('shared.listSeparator'),
        )
        break
    }
  }

  // Show error for invalid url on mount if applicable
  useEffect(() => {
    // If carousel
    if (
      adCreativeTemplateWithRetailerUrl &&
      adCreativeTemplateWithRetailerUrl.creative.child_attachments
    ) {
      adCreativeTemplateWithRetailerUrl.creative.child_attachments.forEach(
        (child, i) => {
          formikRef.current.setFieldTouched(
            `adCreativeTemplate.creative.child_attachments.${i}.link.url`,
            true, // set touched to true
          )
        },
      )
    }
    // If not carousel
    else {
      formikRef.current.setFieldTouched(
        'adCreativeTemplate.creative.link.url',
        true, // set touched to true
      )
    }
    formikRef.current.validateForm()
  }, [adCreativeTemplateWithRetailerUrl])

  return (
    <Formik
      enableReinitialize
      initialValues={{
        adCreativeTemplate: adCreativeTemplateWithRetailerUrl,
        facebookPageId: (facebookPages[0] && facebookPages[0].id) || null,
        carouselIndex: 0,
        radius: currentRetailer.paid_ad_radius || 15,
        fundingAmount: 0,
        // facebookPageId: enabledAutomation
        //   ? Number(enabledAutomation.customization.facebook_page_id)
        //   : (props.facebookPages[0] && props.facebookPages[0].id) || null,
      }}
      ref={formikRef}
      onSubmit={(values, formikActions) => {
        track({
          event: 'Paid Ad Activate Clicked',
          data: {
            campaignId: props.campaignId,
            facebookPageId: values.facebookPageId,
          },
        })
        formikActions.setSubmitting(true)

        const awaitAction = awaitActionType({
          resolve: [ACTIVATE_CAMPAIGN_FULFILLED],
          reject: [ACTIVATE_CAMPAIGN_REJECTED],
          validator: (action) => {
            return action.payload.campaignId === campaign.id
          },
        })

        awaitAction.promise
          .then(() => {
            formikActions.setSubmitting(false)
            setActivationStatus('activated')
            refetchWallet()
            dispatch(
              getChannelCampaign({
                channelCampaignId: campaignChannelId,
                retailerIds: [retailerId],
              }),
            )
          })
          .catch(() => {
            formikActions.setSubmitting(false)
            setActivationStatus('error')
          })

        const fundingSources = []

        if (
          campaign.type === 'brand_and_retailer_funded' ||
          campaign.type === 'retailer_funded'
        ) {
          let amountLeftToSpend = parseInt(values.fundingAmount * 100)
          const mutableLedgers = [...availableLedgers]

          while (amountLeftToSpend > 0) {
            // Assume ledgers are already sorted by "to be used first".
            const ledger = mutableLedgers.shift()

            if (!ledger) {
              break
            }

            const ledgerBalance = parseInt(ledger.balance)

            fundingSources.push({
              amount_cents:
                ledgerBalance >= amountLeftToSpend
                  ? String(amountLeftToSpend)
                  : String(ledgerBalance),
              entity_type: ledger.restrictions[0].key,
              entity_id: ledger.restrictions[0].filter_value,
              ledger_type: 'general',
            })

            amountLeftToSpend -= ledgerBalance
          }

          // We should never get in this situation, as there's validations ahead
          // of time before the form can be submitted.
          if (amountLeftToSpend > 0) {
            throw new Error('amountLeftToSpend was greater than zero')
          }
        }

        dispatch(
          activateCampaign({
            retailerId,
            campaignId: campaign.id,
            facebookPageId: values.facebookPageId,
            radius: values.radius,
            fundingSources,
            businessId: currentRetailerAccount.business_id,
            adcreative_templates: [
              generateCreative({
                adCreativeTemplate: adCreativeTemplateWithRetailerUrl,
                formValues: values,
              }),
            ],
            locations,
          }),
        )
      }}
      validationSchema={schema}
    >
      {({ values }) => (
        <RootContainer {...rootContainerProps}>
          <Grid alignItems="center" className={classes.headerRoot} container>
            <Grid item xs={6}>
              {/* Dropping down to Box / flex here because the UI doesn't work
              out to and sort of "number of columns" here */}
              <Box alignItems="center" display="flex">
                <Box padding={headerMargin}>
                  {isLoading ? (
                    <Skeleton height={40} variant="rectangular" width={40} />
                  ) : (
                    <img alt={headerLine1} height="40" src={brand.logo_url} />
                  )}
                </Box>
                <Box>
                  <Typography variant="h5">{headerLine1}</Typography>
                  <Typography color="textSecondary">{headerLine2}</Typography>
                </Box>
              </Box>
            </Grid>
            <Grid item xs={6}>
              <Box
                alignItems="center"
                display="flex"
                justifyContent="flex-end"
                padding={headerMargin}
              >
                {campaignType === 'retailer_funded'
                  ? undefined
                  : !isLoading && (
                      <PaidAdCredit
                        creditAmount={
                          campaign.campaign_invitation.intended_amount_cents
                        }
                      />
                    )}
                {props.extraButtons}
              </Box>
            </Grid>
          </Grid>
          {activationStatus !== 'activated' && (
            <PremiumAdAutomationBanner id={props.campaignId} />
          )}
          <ContentContainer>
            {/* Intentionally just checking the local activation status */}
            {activationStatus === 'activated' && (
              <Box padding={bodyMargin}>
                <CompletedState brand={brand} campaign={campaign} />
              </Box>
            )}
            {activationStatus !== 'activated' && (
              <Grid container>
                <Grid item md={6} xs={12}>
                  <Box padding={bodyMargin}>
                    {!isLoading && (
                      <PaidAdPreview campaignId={props.campaignId} />
                    )}
                  </Box>
                </Grid>
                <Grid item md={6} xs={12}>
                  <Box padding={bodyMargin}>
                    <Typography variant="h6">
                      {isLoading ? (
                        <Skeleton />
                      ) : (
                        I18n.t('content.campaignObjective')
                      )}
                    </Typography>
                    <Typography color="textSecondary" paragraph variant="body2">
                      {isLoading ? <Skeleton /> : campaign.objective}
                    </Typography>
                  </Box>

                  <Grid container item>
                    <Grid item xs={6}>
                      <Box padding={bodyMargin}>
                        <Typography variant="h6">
                          {isLoading ? (
                            <Skeleton />
                          ) : (
                            I18n.t('content.campaignSchedule')
                          )}
                        </Typography>
                        {isLoading ? (
                          <>
                            <Skeleton />
                            <Skeleton />
                          </>
                        ) : (
                          <>
                            {campaign.starts_at && (
                              <DateHandler
                                date={campaign.starts_at}
                                variation={'start'}
                              />
                            )}
                            {campaign.ends_at && (
                              <DateHandler
                                date={campaign.ends_at}
                                isActivated={isActivated}
                                variation={'end'}
                              />
                            )}
                          </>
                        )}
                      </Box>
                    </Grid>
                    <Grid item xs={6}>
                      <Box padding={bodyMargin}>
                        <Typography variant="h6">
                          {isLoading ? (
                            <Skeleton />
                          ) : (
                            I18n.t('content.targetAudience')
                          )}
                        </Typography>
                        <Typography color="textSecondary" variant="body2">
                          {isLoading ? (
                            <>
                              <Skeleton />
                              <Skeleton />
                              <Skeleton />
                            </>
                          ) : (
                            <>
                              Age: {adSetTemplate.targeting.age_min} -{' '}
                              {adSetTemplate.targeting.age_max}
                              <br />
                              Interests:{' '}
                              {(adSetTemplate.targeting.interests || [])
                                .map((x) => x.name)
                                .join(', ')}
                              <br />
                              Genders: {genderNames}
                            </>
                          )}
                        </Typography>
                      </Box>
                    </Grid>
                  </Grid>
                  {!isLoading &&
                    !isActivated &&
                    !isActivatable &&
                    !locations.length && (
                      <Box padding={bodyMargin}>
                        <Alert
                          action={
                            <Button
                              href={`/#/${currentRetailerAccount.slug}/all/settings?tab=location`}
                              style={{ whiteSpace: 'nowrap' }}
                              variant="outlined"
                            >
                              {I18n.t('content.noLocationsAlertButton')}
                            </Button>
                          }
                          severity="warning"
                        >
                          {I18n.t('content.noLocationsAlertMessage', {
                            brandName: brand.name,
                          })}
                        </Alert>
                      </Box>
                    )}
                  {!isLoading && !isActivated && isActivatable && (
                    <>
                      {isInternal ? (
                        <Box padding={bodyMargin}>
                          <Field
                            component={TextField}
                            disabled={isActivated || !isActivatable}
                            label={I18n.t('content.radius')}
                            name="radius"
                            select
                            fullWidth
                          >
                            <MenuItemWithCheckbox value={5}>
                              5
                            </MenuItemWithCheckbox>
                            <MenuItemWithCheckbox value={15}>
                              15
                            </MenuItemWithCheckbox>
                            <MenuItemWithCheckbox value={25}>
                              25
                            </MenuItemWithCheckbox>
                            <MenuItemWithCheckbox value={50}>
                              50
                            </MenuItemWithCheckbox>
                          </Field>
                        </Box>
                      ) : undefined}

                      {(campaignType === 'brand_and_retailer_funded' ||
                        campaignType === 'retailer_funded') ? (
                        <Alert
                          color={fundingBlockColor}
                          icon={false}
                          sx={{
                            margin: bodyMargin,
                            [`& .${alertClasses.message}`]: {
                              flex: '1',
                            },
                          }}
                        >
                          <AlertTitle>
                            {campaignType === 'retailer_funded'
                              ? I18n.t('content.retailerOnlyFundingMessage')
                              : I18n.t(
                                  'content.retailerAdditionalFundingMessage',
                                )}
                          </AlertTitle>

                          {campaignType === 'brand_and_retailer_funded' ? (
                            <>
                              <Stack
                                direction="row"
                                justifyContent="space-between"
                                marginBottom={1}
                              >
                                <Typography variant="body2">
                                  {I18n.t('content.premiumFundsFromBrand', {
                                    brandName: brand.name,
                                  })}
                                </Typography>
                                <Typography fontWeight="bold" variant="body2">
                                  {formatWithCents.format(
                                    campaign.campaign_invitation
                                      .intended_amount_cents / 100,
                                  )}
                                </Typography>
                              </Stack>

                              <Stack
                                direction="row"
                                justifyContent="space-between"
                                marginBottom={1}
                              >
                                <Typography variant="body2">
                                  {I18n.t('content.additionalAvailableFunds')}
                                </Typography>
                                <Typography fontWeight="bold" variant="body2">
                                  {formatWithCents.format(availableFunds)}
                                </Typography>
                              </Stack>
                            </>
                          ) : undefined}

                          {campaignType === 'retailer_funded' ? (
                            <>
                              <Stack
                                direction="row"
                                justifyContent="space-between"
                                marginBottom={1}
                              >
                                <Typography variant="body2">
                                  {I18n.t('content.minimumSpend')}
                                </Typography>
                                <Typography fontWeight="bold" variant="body2">
                                  {formatWithCents.format(minimumSpend)}
                                </Typography>
                              </Stack>

                              <Stack
                                direction="row"
                                justifyContent="space-between"
                                marginBottom={1}
                              >
                                <Typography variant="body2">
                                  {I18n.t('content.availableFunds')}
                                </Typography>
                                <Typography fontWeight="bold" variant="body2">
                                  {formatWithCents.format(availableFunds)}
                                </Typography>
                              </Stack>
                            </>
                          ) : undefined}

                          <Field
                            component={TextField}
                            name="fundingAmount"
                            type="number"
                            inputProps={{
                              inputMode: 'numeric',
                              pattern: '[0-9.]*',
                            }}
                            label={
                              campaignType === 'brand_and_retailer_funded'
                                ? I18n.t('content.optionalAdditionalFunds')
                                : I18n.t('content.fundingAmount')
                            }
                            fullWidth
                            validate={async (value) => {
                              value = Number(value || 0)
                              const isValueTooLittle = value < minimumSpend
                              const isValueTooHigh = value > availableFunds

                              if (
                                campaignType === 'brand_and_retailer_funded'
                              ) {
                                if (isValueTooHigh) {
                                  return I18n.t(
                                    'content.fundingAmountTooHighError',
                                  )
                                }
                              }

                              if (campaignType === 'retailer_funded') {
                                if (isValueTooLittle) {
                                  return I18n.t(
                                    'content.fundingAmountTooLowError',
                                  )
                                }

                                if (isValueTooHigh) {
                                  return I18n.t(
                                    'content.fundingAmountTooHighError',
                                  )
                                }
                              }

                              return
                            }}
                            value={
                              values.fundingAmount
                                ? `${values.fundingAmount}`.replace(/^0+/, '')
                                : values.fundingAmount
                            }
                            InputProps={{
                              startAdornment: (
                                <InputAdornment position="start">
                                  $
                                </InputAdornment>
                              ),
                            }}
                          />

                          {campaignType === 'brand_and_retailer_funded' ? (
                            <>
                              <Stack
                                direction="row"
                                justifyContent="space-between"
                                marginBottom={1}
                              >
                                <Typography variant="body2">
                                  {I18n.t('content.totalFunding')}
                                </Typography>
                                <Typography fontWeight="bold" variant="body2">
                                  {formatWithCents.format(
                                    campaign.campaign_invitation
                                      .intended_amount_cents /
                                      100 +
                                      values.fundingAmount,
                                  )}
                                </Typography>
                              </Stack>
                            </>
                          ) : undefined}
                          <DepositFundsButton />
                        </Alert>
                      ) : undefined}

                      <Box padding={bodyMargin}>
                        {/*
                        Ah, so FastField doesn't respond to the Field's actual
                        props changed.
                        IE `FastField` doesn't reflect `disabled`.
                        */}
                        <Field
                          component={RetailerOverrides}
                          disabled={isActivated || !isActivatable}
                          name="adCreativeTemplate"
                        />
                      </Box>
                      <Box padding={bodyMargin}>
                        {isAuthenticationAtRaEnabled ? (
                          <>
                            <Field
                              component={TextField}
                              name="facebookPageId"
                              label={I18n.t('content.selectYourFacebookPage')}
                              fullWidth
                              select
                            >
                              <ListSubheader
                                sx={{
                                  bgcolor: 'background.paper',

                                  pointerEvents: 'none',
                                  color: 'text.primary',
                                }}
                              >
                                <Typography variant="subtitle1">
                                  {I18n.t('content.preferredFacebookPage')}
                                </Typography>
                                <Divider />
                              </ListSubheader>
                              {preferredPages.length ? (
                                preferredPages.map((x) => {
                                  return (
                                    <MenuItemWithCheckbox
                                      key={x.id}
                                      value={x.id}
                                    >
                                      {/*
                                    MenuItemWithCheckbox breaks the flex
                                    inherent with MenuItem / ListItem, using
                                    Stack to bring that back
                                    */}
                                      <Stack
                                        direction="row"
                                        spacing={2}
                                        alignItems="center"
                                      >
                                        <ListItemAvatar>
                                          <Avatar
                                            src={x.picture_url}
                                            variant="square"
                                          >
                                            {x.picture_url
                                              ? undefined
                                              : x.name.charAt(0)}
                                          </Avatar>
                                        </ListItemAvatar>
                                        <ListItemText primary={x.name} />
                                      </Stack>
                                    </MenuItemWithCheckbox>
                                  )
                                })
                              ) : (
                                <MenuItem value={null} disabled>
                                  <Typography variant="subtitle2">
                                    {I18n.t('content.noPreferredFacebookPage')}
                                  </Typography>
                                </MenuItem>
                              )}
                              {unpreferredPages.length && (
                                <ListSubheader sx={{ paddingBottom: 2 }}>
                                  <Divider />
                                </ListSubheader>
                              )}
                              {unpreferredPages.map((x) => {
                                return (
                                  <MenuItemWithCheckbox key={x.id} value={x.id}>
                                    {/*
                                    MenuItemWithCheckbox breaks the flex
                                    inherent with MenuItem / ListItem, using
                                    Stack to bring that back
                                    */}
                                    <Stack
                                      direction="row"
                                      spacing={2}
                                      alignItems="center"
                                    >
                                      <ListItemAvatar>
                                        <Avatar
                                          src={x.picture_url}
                                          variant="square"
                                        >
                                          {x.picture_url
                                            ? undefined
                                            : x.name.charAt(0)}
                                        </Avatar>
                                      </ListItemAvatar>
                                      <ListItemText primary={x.name} />
                                    </Stack>
                                  </MenuItemWithCheckbox>
                                )
                              })}
                            </Field>
                          </>
                        ) : (
                          <>
                            <Field
                              component={ChannelPageSelectorField}
                              facebookPages={facebookPages}
                              name="facebookPageId"
                            />
                          </>
                        )}
                        <AutomationBrandFacebookPageCheckbox
                          brandId={campaign.brand_id}
                          facebookPageId={values.facebookPageId}
                        />
                      </Box>
                      <Box padding={bodyMargin}>
                        <PaidAdCTA
                          campaign={campaign}
                          disabled={isActivated || !isActivatable}
                          facebookPageId={values.facebookPageId}
                          facebookPages={facebookPages}
                          locations={locations}
                        />
                      </Box>
                    </>
                  )}
                </Grid>
              </Grid>
            )}
          </ContentContainer>
        </RootContainer>
      )}
    </Formik>
  )
}

function mergeRetailerOverride(adCreativeTemplate, retailerUrl, isActivated) {
  if (!adCreativeTemplate) {
    return
  }

  if (!retailerUrl) {
    return adCreativeTemplate
  }

  const cloned = JSON.parse(JSON.stringify(adCreativeTemplate))

  if (cloned.creative_type === 'carousel') {
    for (const childAttachment of cloned.creative.child_attachments) {
      if (childAttachment.link.link_type === 'retailer_website') {
        if (!isActivated) {
          childAttachment.link.url = retailerUrl
        }
      }
    }
  } else {
    if (cloned.creative.link.link_type === 'retailer_website') {
      if (!isActivated) {
        cloned.creative.link.url = retailerUrl
      }
    }
  }
  return cloned
}

export default memo(FPPAP)
